import React from "react";
import { Box, Button, Checkbox, Chip, FormControlLabel, Paper, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import ExcelJS from "exceljs/dist/exceljs.min.js";
import { enqueueSnackbar } from "notistack";
import { DataTable, SelectField } from "../../Layout/Common";
import { FIXED_TABLE, TIERS, TIER_NAMES, tierOf } from "./loc";

const SHEET = "Strings";
const mono = { fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 };

// The xlsx round trip, built in the browser: export = the backend's rows (`export`) as one sheet "Strings" with the
// columns tag, note, then one per language (the tier and the outdated flag as a cell note); import = a workbook of
// that shape parsed here, previewed against the backend's dry run (`import`), then applied at the tier chosen.
export default class ImportExportTab extends React.Component {
    state = {
        langs: [], folder: "*", tier: "", outdated: false, building: false, lastRows: null, lastName: "",
        rows: null, fileName: "", importTier: "HumanGeneric", createMissing: false, preview: null, previewing: false, applying: false,
    };
    fileInput = React.createRef();

    componentDidMount() {
        const { project } = this.props;
        this.setState({ langs: project.languages.map(l => l.code).filter(c => c !== project.baseLanguage) });
    }
    reload() {}

    // ---------- export ----------
    export = () => {
        const { langs, folder, tier, outdated } = this.state;
        const payload = { languages: langs, folders: folder && folder !== "*" ? [folder] : [], tier: tier === "" ? null : Number(tier), outdated: outdated || null };
        this.setState({ building: true });
        this.props.loc("Exporting", ["export"], { payload, silent: true, timeout: 90000 })
            .then(async r => {
                if (typeof r === "string") { enqueueSnackbar(r, { variant: "error" }); return; }
                const name = `${this.props.project.titleId}-strings${folder && folder !== "*" ? "-" + folder.replace(/\//g, "_") : ""}.xlsx`;
                const buffer = await this.build(r);
                this.setState({ lastRows: r, lastName: name });
                this.download(buffer, name);
                enqueueSnackbar(`${r.rows.length} rows, ${r.languages.length} languages`, { variant: "success" });
            })
            .catch(e => enqueueSnackbar(e.message, { variant: "error" }))
            .finally(() => this.setState({ building: false }));
    }

    async build(data) {
        const wb = new ExcelJS.Workbook();
        wb.creator = "gm.aurecas.com";
        const ws = wb.addWorksheet(SHEET, { views: [{ state: "frozen", ySplit: 1, xSplit: 1 }] });
        const langs = data.languages;
        ws.columns = [{ header: "tag", key: "tag", width: 36 }, { header: "note", key: "note", width: 28 }, ...langs.map(l => ({ header: l, key: l, width: 40 }))];
        ws.getRow(1).font = { bold: true };
        for (const r of data.rows) {
            const row = ws.addRow({ tag: r.path, note: r.note ?? "" });
            langs.forEach((l, i) => {
                const cell = row.getCell(3 + i);
                const t = r.texts?.[l];
                cell.value = t?.text ?? "";
                cell.alignment = { wrapText: true, vertical: "top" };
                if (t) {
                    const tier = tierOf(t.tier);
                    cell.note = `${tier.label}${t.outdated ? " · outdated" : ""}`;
                    if (t.outdated) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFE0B2" } };
                }
            });
        }
        return wb.xlsx.writeBuffer();
    }

    download(buffer, name) {
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = name; document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
    }

    // The export just built, parsed back and checked against the backend: every row must come back unchanged
    roundTrip = async () => {
        const { lastRows } = this.state;
        if (!lastRows) return;
        const buffer = await this.build(lastRows);
        const rows = await this.parse(buffer);
        // at the lowest tier: the same text never lowers a cell's tier, so a clean export comes back all unchanged
        this.setState({ rows, fileName: this.state.lastName + " (round trip)", preview: null, importTier: "AiIsolated", createMissing: false }, () => this.previewImport(true));
    }

    // ---------- import ----------
    pickFile = () => this.fileInput.current?.click();
    onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        try {
            const rows = await this.parse(await file.arrayBuffer());
            this.setState({ rows, fileName: file.name, preview: null });
            enqueueSnackbar(`${rows.length} rows read from ${file.name}`, { variant: "info" });
        } catch (err) { enqueueSnackbar(`Could not read the workbook: ${err.message}`, { variant: "error" }); }
    }

    async parse(buffer) {
        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(buffer);
        const ws = wb.getWorksheet(SHEET) ?? wb.worksheets[0];
        if (!ws) throw new Error("no sheet");
        const header = [];
        ws.getRow(1).eachCell({ includeEmpty: false }, (c, i) => { header[i] = String(c.value ?? "").trim(); });
        const tagCol = header.indexOf("tag"), noteCol = header.indexOf("note");
        if (tagCol < 1) throw new Error("the first row needs a 'tag' column");
        const langCols = header.map((h, i) => ({ h, i })).filter(x => x.i > 0 && x.i !== tagCol && x.i !== noteCol && /^[A-Z]{2,3}$/.test(x.h));
        const rows = [];
        const text = (c) => { const v = c?.value; if (v == null) return null; if (typeof v === "object") return v.richText ? v.richText.map(t => t.text).join("") : v.text ?? String(v); return String(v); };
        ws.eachRow((row, n) => {
            if (n === 1) return;
            const path = (text(row.getCell(tagCol)) ?? "").trim();
            if (!path) return;
            const texts = {};
            langCols.forEach(({ h, i }) => { const t = text(row.getCell(i)); if (t != null && t !== "") texts[h] = t; });
            const r = { path, texts };
            if (noteCol > 0) { const note = text(row.getCell(noteCol)); if (note != null) r.note = note; }
            rows.push(r);
        });
        return rows;
    }

    previewImport = (silent = false) => {
        const { rows, importTier, createMissing } = this.state;
        if (!rows) return;
        this.setState({ previewing: true });
        this.props.loc("Checking the rows", ["import"], { payload: { rows, tier: importTier, createMissing, dryRun: true }, silent: true, timeout: 120000 })
            .then(r => {
                if (typeof r === "string") { enqueueSnackbar(r, { variant: "error" }); return; }
                this.setState({ preview: r });
                if (silent) enqueueSnackbar(r.counts.changed === 0 && r.counts.new === 0 && r.counts.rejected === 0 ? "Round trip clean: every row comes back unchanged" : `Round trip: ${r.counts.changed} changed, ${r.counts.rejected} rejected`, { variant: r.counts.changed === 0 && r.counts.rejected === 0 ? "success" : "warning" });
            })
            .catch(e => enqueueSnackbar(e.message, { variant: "error" }))
            .finally(() => this.setState({ previewing: false }));
    }

    apply = () => {
        const { rows, importTier, createMissing } = this.state;
        this.setState({ applying: true });
        this.props.loc("Importing", ["import"], { payload: { rows, tier: importTier, createMissing, dryRun: false }, retry: false, timeout: 180000 })
            .then(r => {
                if (typeof r === "string") { enqueueSnackbar(r, { variant: "error" }); return; }
                enqueueSnackbar(`Imported: ${r.counts.new} new, ${r.counts.changed} changed, ${r.counts.kept} kept, ${r.counts.rejected} rejected`, { variant: "success" });
                this.setState({ rows: null, preview: null, fileName: "" });
                this.props.onImported();
            })
            .catch(() => {})
            .finally(() => this.setState({ applying: false }));
    }

    render() {
        const { project, tree, canEdit } = this.props;
        const { langs, folder, tier, outdated, building, lastRows, rows, fileName, importTier, createMissing, preview, previewing, applying } = this.state;
        const codes = project.languages.map(l => l.code).filter(c => c !== project.baseLanguage);
        const folderOptions = [{ value: "*", label: "(every folder)" }, ...tree.folders.slice().sort((a, b) => a.order - b.order).map(f => ({ value: f.path, label: `${f.path} (${f.tags})` }))];
        const tierOptions = [{ value: "", label: "(any tier)" }, ...TIERS.slice(1).map(t => ({ value: String(t.value), label: t.label }))];
        const importTiers = TIER_NAMES.slice(1).map((n, i) => ({ value: n, label: TIERS[i + 1].label }));
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 1200 }}>
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Export</Typography>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 1 }}>
                        <SelectField label="Folder" size="small" minWidth={260} value={folder} options={folderOptions} onChange={v => this.setState({ folder: v })} />
                        <SelectField label="Only tags with a cell at tier" size="small" minWidth={220} value={tier} options={tierOptions} onChange={v => this.setState({ tier: v })} />
                        <FormControlLabel control={<Checkbox size="small" checked={outdated} onChange={e => this.setState({ outdated: e.target.checked })} />} label="only outdated" />
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Languages ({project.baseLanguage} always):</Typography>
                        {codes.map(c => <Chip key={c} size="small" label={c} variant={langs.includes(c) ? "filled" : "outlined"} color={langs.includes(c) ? "primary" : "default"} onClick={() => this.setState({ langs: langs.includes(c) ? langs.filter(l => l !== c) : [...langs, c] })} />)}
                        <Button size="small" onClick={() => this.setState({ langs: codes })}>all</Button>
                        <Button size="small" onClick={() => this.setState({ langs: [] })}>none</Button>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Button variant="contained" startIcon={<DownloadIcon />} onClick={this.export} disabled={building}>{building ? "Building…" : "Export xlsx"}</Button>
                        {lastRows && <Button size="small" onClick={this.roundTrip} disabled={previewing}>Check the round trip</Button>}
                        <Typography variant="caption" color="text.secondary">One sheet “Strings”: tag, note, one column per language; the tier and the outdated flag are cell notes, outdated cells are orange.</Typography>
                    </Box>
                </Paper>
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Import</Typography>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                        <Button variant="outlined" startIcon={<UploadIcon />} onClick={this.pickFile} disabled={!canEdit}>Pick a workbook</Button>
                        <input ref={this.fileInput} type="file" accept=".xlsx" hidden onChange={this.onFile} />
                        <Typography variant="body2" sx={mono}>{fileName || "—"}</Typography>
                        {rows && <Typography variant="caption" color="text.secondary">{rows.length} rows</Typography>}
                        <SelectField label="Tier of the imported cells" size="small" minWidth={220} value={importTier} options={importTiers} onChange={v => this.setState({ importTier: v, preview: null })} disabled={!rows} />
                        <FormControlLabel control={<Checkbox size="small" checked={createMissing} onChange={e => this.setState({ createMissing: e.target.checked, preview: null })} disabled={!rows} />} label="create tags the sheet has and the backend lacks" />
                        <Button variant="contained" onClick={() => this.previewImport(false)} disabled={!rows || previewing}>{previewing ? "Checking…" : "Preview"}</Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                        Empty cells are left alone. The base language is always written at human specialized; a cell at a higher tier than the one chosen is kept (listed below).
                        A changed base text marks the languages the sheet does not carry as outdated.
                    </Typography>
                    {preview && (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 1 }}>
                                <Chip size="small" color="success" label={`${preview.counts.new} new`} />
                                <Chip size="small" color="info" label={`${preview.counts.changed} changed`} />
                                <Chip size="small" label={`${preview.counts.unchanged} unchanged`} />
                                <Chip size="small" color="warning" label={`${preview.counts.kept} kept`} />
                                <Chip size="small" color="error" label={`${preview.counts.rejected} rejected`} />
                                <Box sx={{ flex: 1 }} />
                                {canEdit && <Button variant="contained" color="primary" onClick={this.apply} disabled={applying || (preview.counts.new + preview.counts.changed === 0)}>{applying ? "Importing…" : `Apply at ${importTier}`}</Button>}
                            </Box>
                            {preview.new.length > 0 && <Typography variant="body2" sx={{ mb: 1 }}><b>New:</b> <span style={mono}>{preview.new.slice(0, 50).join(", ")}{preview.new.length > 50 ? " …" : ""}</span></Typography>}
                            {preview.changed.length > 0 && <DataTable sx={{ mb: 1, ...FIXED_TABLE }} columns={[
                                { id: "path", label: "Changed tag", width: "40%", render: r => <span style={mono}>{r.path}</span> },
                                { id: "cells", label: "Cells", width: "25%", render: r => r.cells.join(", ") },
                                { id: "base", label: "", width: "35%", render: r => r.baseChanged ? <Chip size="small" color="warning" label={`base text; outdates ${r.outdated.join(", ") || "nothing"}`} /> : "" },
                            ]} rows={preview.changed.slice(0, 200)} getRowKey={r => r.path} />}
                            {preview.kept.length > 0 && <DataTable sx={{ mb: 1, ...FIXED_TABLE }} columns={[
                                { id: "path", label: "Kept (higher tier)", width: "35%", render: r => <span style={mono}>{r.path}</span> },
                                { id: "lang", label: "", width: "10%", render: r => r.lang },
                                { id: "reason", label: "Reason", width: "55%", render: r => r.reason },
                            ]} rows={preview.kept.slice(0, 200)} getRowKey={(r, i) => i} />}
                            {preview.rejected.length > 0 && <DataTable sx={FIXED_TABLE} columns={[
                                { id: "path", label: "Rejected", width: "35%", render: r => <span style={mono}>{r.path}</span> },
                                { id: "lang", label: "", width: "10%", render: r => r.lang ?? "" },
                                { id: "reason", label: "Reason", width: "55%", render: r => r.reason },
                            ]} rows={preview.rejected.slice(0, 200)} getRowKey={(r, i) => i} />}
                        </Box>
                    )}
                </Paper>
            </Box>
        );
    }
}
